/**
 * Multi-Agent System domain and agent definitions.
 *
 * Three domains: CI/CD, IaC, Observability.
 * Each domain has 7 agents with named skills.
 */

export const agentDomains = {
  cicd: {
    name: 'CI/CD Agents',
    color: '#38bdf8',
    agents: [
      { name: 'Pipeline Architect', role: 'Pipeline generation, optimization, migration', skills: 6 },
      { name: 'Test Strategist', role: 'Test planning, coverage analysis, flaky detection', skills: 6 },
      { name: 'Release Guardian', role: 'Release orchestration, rollback, feature flags', skills: 6 },
      { name: 'Security Scanner', role: 'SAST/DAST, dependency scanning, secret detection', skills: 5 },
      { name: 'Artifact Manager', role: 'Build optimization, caching, registry management', skills: 5 },
      { name: 'Deploy Strategist', role: 'Deployment strategies, canary, blue-green', skills: 6 },
      { name: 'Feedback Analyst', role: 'Metrics collection, deployment analysis, insights', skills: 5 },
    ],
  },
  iac: {
    name: 'IaC Agents',
    color: '#a78bfa',
    agents: [
      { name: 'Module Architect', role: 'Terraform module generation, refactoring, validation', skills: 6 },
      { name: 'Drift Watcher', role: 'Drift detection, classification, reconciliation', skills: 5 },
      { name: 'Cost Sentinel', role: 'Cost estimation, budget enforcement, FinOps', skills: 6 },
      { name: 'Policy Guardian', role: 'Security scanning, OPA/Sentinel, compliance', skills: 6 },
      { name: 'State Doctor', role: 'State management, recovery, migration', skills: 6 },
      { name: 'Environment Orchestrator', role: 'Environment lifecycle, promotion, TTL', skills: 6 },
      { name: 'Change Planner', role: 'Plan analysis, risk assessment, approvals', skills: 6 },
    ],
  },
  obs: {
    name: 'Observability Agents',
    color: '#34d399',
    agents: [
      { name: 'Alert Architect', role: 'Alert rule generation, noise detection, routing', skills: 6 },
      { name: 'Incident Analyst', role: 'Signal correlation, root cause analysis, postmortems', skills: 6 },
      { name: 'Dashboard Designer', role: 'Dashboard generation, optimization, SLO views', skills: 6 },
      { name: 'Metric Curator', role: 'Golden signals, cardinality, recording rules', skills: 6 },
      { name: 'Log Investigator', role: 'Pattern analysis, anomaly detection, optimization', skills: 6 },
      { name: 'Trace Navigator', role: 'Latency analysis, service maps, critical paths', skills: 6 },
      { name: 'SLO Guardian', role: 'SLO definition, error budgets, burn rate alerts', skills: 6 },
    ],
  },
};

export const maturityStages = [
  { stage: 'Exploration', desc: 'Engineers use ChatGPT/Copilot for ad-hoc help. No structure.', color: 'var(--text-dim)' },
  { stage: 'Foundation', desc: 'Agent skills invoked manually — engineer asks AI to review a plan or estimate cost.', color: 'var(--blue)' },
  { stage: 'Application', desc: 'Individual agents embedded in CI — Policy Guardian scans every PR automatically.', color: 'var(--violet)' },
  { stage: 'Standardization', desc: 'MCP servers connect agents to Terraform Cloud, cloud APIs. Golden configs curated.', color: 'var(--green)' },
  { stage: 'Integration', desc: 'Multi-agent workflows active. Full stories: PR → security + cost + risk → approval.', color: 'var(--orange)' },
  { stage: 'Operationalization', desc: 'Autonomous end-to-end. Low-risk changes auto-applied. Humans review high-risk only.', color: 'var(--pink)' },
];

export const learningPath = [
  { phase: '1', title: 'Understand the Basics', duration: '1-2 weeks', color: '#38bdf8', items: ['What LLMs are and how they work', 'Prompt engineering fundamentals', 'Tokens, context windows, temperature', 'API basics (Anthropic, OpenAI)'] },
  { phase: '2', title: 'Build Your First Agent', duration: '2-3 weeks', color: '#a78bfa', items: ['Single skill: prompt → LLM → structured output', 'Pydantic schemas for type safety', 'Error handling and retries', 'Unit testing with mocked LLM'] },
  { phase: '3', title: 'Connect to Tools (MCP)', duration: '2-3 weeks', color: '#34d399', items: ['MCP protocol basics', 'Build an MCP server for your tool', 'Connect agents to external services', 'Handle auth and rate limits'] },
  { phase: '4', title: 'Add Knowledge (RAG)', duration: '2-3 weeks', color: '#fb923c', items: ['Vector embeddings and search', 'Build a knowledge base from your docs', 'Inject context into prompts', 'Measure retrieval quality'] },
  { phase: '5', title: 'Multi-Agent Orchestration', duration: '3-4 weeks', color: '#f472b6', items: ['Workflow orchestrator with sequencing', 'Shared context between agents', 'Parallel execution and branching', 'Event-driven agent communication'] },
  { phase: '6', title: 'Production Guardrails', duration: '2-3 weeks', color: '#fbbf24', items: ['Autonomy levels per environment', 'Kill switches and approval gates', 'Rate limiting and escalation rules', 'Monitoring and observability for agents'] },
];
